import { BASE_URL } from 'utils/apiFunctions';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Role hierarchy from highest to lowest permissions
const ROLE_HIERARCHY = ['SYSTEM_ADMIN', 'ADMIN', 'TEAM_MANAGER', 'PLAYER'];

function getHighestPermissionRole(userRoles) {
    // Find the role with the highest permissions
    for (const role of ROLE_HIERARCHY) {
        if (userRoles.includes(role)) {
            return role;
        }
    }
    // Fallback to first role if none match the hierarchy
    return userRoles[0];
}

const useLoginStore = create(
    persist(
        (set, get) => ({

    isLoggedIn: null,
    user: null,
    roles: null,
    currentRole: null,
    managedTeams: null,

    updateIsLoggedIn: async () => {
    
        try {
            // console.log('UpdateIsLoggedIn...')

            const response = await fetch(`${BASE_URL}/auth/isLoggedIn`, {
                method: 'GET',
                credentials: 'include',
            });

            const respBody = await response.json();
            const userRoles = respBody?.user?.roles?.split(',') || [];
            
            // Get the persisted role and validate it's still available for this user
            const persistedRole = get().currentRole;
            let currentRole;
            
            if (persistedRole && userRoles.includes(persistedRole)) {
                // Use the persisted role if it's still valid
                currentRole = persistedRole;
            } else {
                // Fall back to default logic if no valid persisted role
                currentRole = userRoles.length === 1 ? userRoles[0] : 
                             (userRoles.length > 1 ? getHighestPermissionRole(userRoles) : null);
            }
            
            set({
                isLoggedIn: (respBody?.user) ? true : false,
                user: respBody?.user,
                roles: userRoles,
                currentRole: currentRole,
                managedTeams: respBody?.user?.ManagedTeams,
            });

            // console.log('UpdateIsLoggedIn = ' + ((respBody?.user) ? true : false))

            return {
                status: response.status,
                body: respBody,
            }
        } catch (e) {
            console.error(e);
        }
    },

    login: async (data) => {
    
        try {
            const response = await fetch(`${BASE_URL}/auth/login`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            get().updateIsLoggedIn();

            const respBody = await response.json();
            return {
                status: response.status,
                body: respBody,
            }
        } catch (e) {
            console.error(e);
            return {
                status: null,
                body: null,
            };
        }
    },

    logout: async () => {
        try {
            const response = await fetch(`${BASE_URL}/auth/logout`, {
                method: 'POST',
                credentials: 'include',
            });

            // Clear the current role before updating login status
            set({ currentRole: null });
            get().updateIsLoggedIn();

            const respBody = await response.json();
            return {
                status: response.status,
                body: respBody,
            }
        } catch (e) {
            console.error(e);
            return {
                status: null,
                body: null,
            };
        }
    },

    verifyPasswordResetRequest: async ({ passwordResetRequestId }) => {
        try {
            const response = await fetch(`${BASE_URL}/auth/verifyPasswordResetRequest?passwordResetRequestId=${passwordResetRequestId}`, {
                method: 'GET',
                credentials: 'include',
            });

            const respBody = await response.json();
            return {
                status: response.status,
                body: respBody,
            }
        } catch (e) {
            console.error(e);
            return {
                status: null,
                body: null,
            };
        }
    },

    forgotPassword: async ({ email }) => {
        try {
            const response = await fetch(`${BASE_URL}/auth/forgotPassword`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                }),
            });

            const respBody = await response.json();
            return {
                status: response.status,
                body: respBody,
            }
        } catch (e) {
            console.error(e);
            return {
                status: null,
                body: null,
            };
        }
    },

    resetPassword: async ({ passwordResetRequestId, newPassword }) => {
        try {
            const response = await fetch(`${BASE_URL}/auth/resetPassword`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    passwordResetRequestId,
                    newPassword,
                }),
            });

            const respBody = await response.json();
            return {
                status: response.status,
                body: respBody,
            }
        } catch (e) {
            console.error(e);
            return {
                status: null,
                body: null,
            };
        }
    },


    isAdmin: () => {
        const currentRole = get().currentRole;
        return currentRole === 'SYSTEM_ADMIN' || currentRole === 'ADMIN';
    },

    isOrgAdmin: () => {
        const currentRole = get().currentRole;
        return currentRole === 'ADMIN';
    },

    isSystemAdmin: () => {
        const currentRole = get().currentRole;
        return currentRole === 'SYSTEM_ADMIN';
    },

    isTeamManager: () => {
        const currentRole = get().currentRole;
        return currentRole === 'TEAM_MANAGER';
    },

    isPlayer: () => {
        const currentRole = get().currentRole;
        return currentRole === 'PLAYER';
    },

    setCurrentRole: (role) => {
        const roles = get().roles;
        if (roles && roles.includes(role)) {
            set({ currentRole: role });
        }
    },

    getCurrentRole: () => {
        return get().currentRole;
    },

    hasMultipleRoles: () => {
        const roles = get().roles;
        return roles && roles.length > 1;
    },

    getAvailableRoles: () => {
        const roles = get().roles;
        return roles || [];
    },
        }),
        {
            name: 'loginStore',
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                currentRole: state.currentRole,
            }),
        }
    )
);

export default useLoginStore;
